import { CounterIcon, LockerIcon, MachineIcon } from "../../../../../common/Icons";
import { MONITOR_COLORS } from "../../config/functions";
import { Box, Chip, Typography, IconButton } from "@mui/material";
import { Wifi, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { useState, useRef, useEffect } from "react";

const typeConfig = {
    machine: {
        label: 'Máquina',
        backgroundColor: MONITOR_COLORS.background.machine,
        icon: MachineIcon,
    },
    counter: {
        label: 'Balcão',
        backgroundColor: MONITOR_COLORS.background.counter,
        icon: CounterIcon,
    },
    locker: {
        label: 'Armário',
        backgroundColor: MONITOR_COLORS.background.locker,
        icon: LockerIcon,
    }
};

function NoSyncEquipment({ equipamento }) {
    const config = typeConfig[equipamento.tipo] ?? typeConfig.machine;
    const Icon = config.icon;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 3,
            borderBottom: '1px solid ' + MONITOR_COLORS.tertiary + '55',
            '&:last-child': { borderBottom: 'none' },
            flexShrink: 0,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ position: 'relative', flexShrink: 0, marginRight: '1vw' }}>
                    <Icon sx={{ width: 20, height: 20, color: config.color }} />
                    <Box sx={{
                        position: 'absolute',
                        bottom: -3,
                        right: -3,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: MONITOR_COLORS.status.warning.secondary,
                        border: '2px solid ' + MONITOR_COLORS.secondary,
                    }} />
                </Box>

                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: { xs: '0.8rem', xl: '0.85rem' }, fontWeight: 600, color: MONITOR_COLORS.text.primary }}>
                            {equipamento.nome + ' - ' + equipamento.tag}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.65rem',
                            color: MONITOR_COLORS.text.secondary,
                            backgroundColor: config.color + '22',
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 1,
                        }}>
                            ({config.label})
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.secondary }}>
                        {equipamento.empresa}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.secondary, flexShrink: 0, ml: 1 }}>
                    {equipamento.tempo_atras}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: MONITOR_COLORS.text.primary, flexShrink: 0, ml: 1 }}>
                    {equipamento.horario}
                </Typography>
            </Box>
        </Box>
    );
}

export function MonitorNoSyncEquipment({ noSyncEquipment }) {
    const equipamentos = noSyncEquipment?.equipamentos ?? [];
    const total = noSyncEquipment?.total ?? 0;
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(4);
    const containerRef = useRef(null);

    useEffect(() => { setPage(0); }, [equipamentos.length]);

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                // Cada linha tem ~56px de altura
                const rows = Math.max(1, Math.floor(height / 56));
                setItemsPerPage(rows);
            }
        });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const totalPages = Math.ceil(equipamentos.length / itemsPerPage);
    const paginated = equipamentos.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
    const canGoUp = page > 0;
    const canGoDown = page < totalPages - 1;

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            width: 'calc(100% - 1vw)',
            height: '100%',
            marginTop: '1vh'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                flexShrink: 0,
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid ' + MONITOR_COLORS.tertiary,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Wifi sx={{ color: MONITOR_COLORS.text.primary, fontSize: { xs: 18, xl: 22 }, marginRight: '5vw' }} />
                    <Typography sx={{ fontWeight: 700, color: MONITOR_COLORS.text.primary, fontSize: { xs: '0.9rem', xl: '1rem' } }}>
                        Sem Sincronização
                    </Typography>
                </Box>

                {total > 0 && (
                    <Chip
                        label={total}
                        size="small"
                        sx={{
                            backgroundColor: MONITOR_COLORS.status.warning.primary,
                            color: MONITOR_COLORS.status.warning.secondary,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            height: 24,
                        }}
                    />
                )}
            </Box>

            {/* Corpo: lista + botões */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
                {/* Lista */}
                <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {equipamentos.length === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '0.8rem', color: MONITOR_COLORS.text.secondary }}>
                                Todos sincronizados ✓
                            </Typography>
                        </Box>
                    ) : (
                        paginated.map((eq, i) => (
                            <NoSyncEquipment key={i} equipamento={eq} />
                        ))
                    )}
                </Box>

                {/* Botões de navegação */}
                {totalPages > 1 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: 1,
                        borderLeft: '1px solid ' + MONITOR_COLORS.tertiary + '55',
                    }}>
                        <Typography sx={{ fontSize: '0.65rem', color: MONITOR_COLORS.text.secondary, fontFamily: 'monospace' }}>
                            {page + 1}/{totalPages}
                        </Typography>

                        <IconButton
                            onClick={() => setPage(p => p - 1)}
                            disabled={!canGoUp}
                            size="small"
                            sx={{
                                color: canGoUp ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoUp ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': { backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '66' : 'transparent' },
                                transition: 'all 0.2s',
                                width: 28,
                                height: 28,
                            }}
                        >
                            <KeyboardArrowUp fontSize="small" />
                        </IconButton>

                        <IconButton
                            onClick={() => setPage(p => p + 1)}
                            disabled={!canGoDown}
                            size="small"
                            sx={{
                                color: canGoDown ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoDown ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': { backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '66' : 'transparent' },
                                transition: 'all 0.2s',
                                width: 28,
                                height: 28,
                            }}
                        >
                            <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}